// @flow
/* eslint-disable jsx-a11y/no-autofocus */

import keycode from 'keycode';
import React from 'react';
import AtomicInput from './AtomicInput';
import InputApi from './InputApi';
import type { AtomicInputProps } from './AtomicInput';

type TextInputProps = AtomicInputProps<string> & {
  noTrimming: boolean,
  pattern: RegExp,
  placeholder: string,
  onBlur: (InputApi<string>) => void,
  onFocus: (InputApi<string>) => void,
  autoFocus?: boolean,
  // The full list of values is enormous, and the tokens can be combined using spaces
  // Flow cannot express this, so `string` will have to do here.
  autoComplete?: 'on' | 'off' | string,
  autoSelect?: boolean,
  commitOnBlur: boolean,
  maxLength?: number,
};

export default class TextInput extends AtomicInput<string, string, TextInputProps> {
  static defaultProps = {
    ...AtomicInput.defaultProps,
    required: false,
    noTrimming: false,
    placeholder: '',
    autoFocus: false,
    autoSelect: false,
    commitOnBlur: true,
    maxLength: 256,
  };

  inputType = 'text';

  inputElementRef = React.createRef();

  focus() {
    if (this.inputElementRef.current) {
      this.inputElementRef.current.focus();
    }
  }

  domValueToValue(rawValue: string): string {
    return rawValue;
  }

  valueToDomValue(value: string): string {
    return value;
  }

  additionalInputProps(): { [string]: string } {
    const additionalProps = {};
    if (this.props.className) {
      additionalProps.className = this.props.className;
    }
    if (this.props.placeholder) {
      additionalProps.placeholder = this.props.placeholder;
    }

    return additionalProps;
  }

  runBuiltinValidations() {
    super.runBuiltinValidations();
    if ('pattern' in this.props && !this.props.pattern.test(this.currentValue)) {
      this.addValidationProblem({
        name: 'PATTERN',
        message: `Value "${this.currentValue}" does not match pattern /${this.props.pattern.source}/`,
        value: this.currentValue,
        inputName: [],
      });
    }
  }

  cleanValue(value: string): string {
    if (this.props.noTrimming) {
      return value;
    }
    return value.trim();
  }

  render() {
    return (
      <input
        ref={this.inputElementRef}
        autoFocus={this.props.autoFocus}
        autoComplete={this.props.autoComplete}
        className={this.props.className}
        type={this.inputType}
        placeholder={this.props.placeholder}
        value={this.domValue}
        maxLength={this.props.maxLength}
        name={this.parent ? this.name : ''}
        onChange={ev => {
          this.inputDidChange(ev.target.value);
        }}
        onKeyDown={(ev: SyntheticKeyboardEvent<HTMLInputElement>) => {
          if (keycode(ev) === 'enter') {
            this.inputDidCommitChange();
          }
        }}
        onBlur={() => {
          if (this.props.onBlur && typeof this.props.onBlur === 'function') {
            this.props.onBlur(this.api);
          }
          if (this.props.commitOnBlur) {
            this.inputDidCommitChange();
          }
        }}
        onFocus={ev => {
          if (this.props.autoSelect) {
            ev.target.select();
          }
          if (this.props.onFocus && typeof this.props.onFocus === 'function') {
            this.props.onFocus(this.api);
          }
        }}
      />
    );
  }
}
